import {
  isValidLocaleCode,
  LocaleCode,
  LOCALE_OPTIONS,
} from '@choco-admin/types';
import React from 'react';

import { ChocoSelect } from '../ChocoSelect';

export interface LocaleSelectFieldProps {
  value?: LocaleCode;
  onChange: (locale: LocaleCode) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  errorMessage?: string;
  helperText?: string;
  'data-testid'?: string;
}

export const LocaleSelectField: React.FC<LocaleSelectFieldProps> = ({
  value,
  onChange,
  isDisabled = false,
  isRequired = false,
  label = 'Language',
  placeholder = 'Choose language...',
  errorMessage,
  helperText,
  'data-testid': dataTestId,
}) => {
  // Convert LOCALE_OPTIONS to ChocoSelect format
  const knownOptions = LOCALE_OPTIONS.map(
    (option: { key: LocaleCode; label: string }) => ({
      value: option.key,
      label: option.label,
    })
  );

  // A stored value with no option of its own would render as an empty required
  // field, which reads as "no language set" and pushes the reader into picking
  // a replacement. Naming it keeps the current setting visible and leaves the
  // choice to change it deliberate.
  const isUnlisted =
    !!value && !knownOptions.some((option) => option.value === value);
  const selectOptions = isUnlisted
    ? [...knownOptions, { value, label: value }]
    : knownOptions;

  return (
    <div data-testid={dataTestId}>
      <ChocoSelect
        label={label}
        placeholder={placeholder}
        options={selectOptions}
        value={value || ''}
        onSelectionChange={(selectedValue) => {
          if (selectedValue && isValidLocaleCode(selectedValue)) {
            onChange(selectedValue);
          }
        }}
        isDisabled={isDisabled}
        isRequired={isRequired}
        errorMessage={errorMessage}
        description={helperText}
        showDescriptions={false}
      />
    </div>
  );
};
